import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {Table, Button, message, Modal, Input, Tooltip} from 'antd';
import ImageUploaderEnhance from './ImageUploaderEnhance';
import {CheckOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {useMutation, useQuery} from '@apollo/client';
import {PickupAppointmentType} from '../graphql/queries/getPickupAppointmentListing';
import {CANCEL_PICKUP_APPOINTMENT, CancelPickupAppointmentResponse} from '../graphql/mutations/cancelPickupAppointment';
import {UPDATE_APPOINTMENT_NOTES, UpdateAppointmentNotesResponse} from '../graphql/mutations/updateAppointmentNotes';
import PickupAppointmentParcelsDrawer from './PickupAppointmentParcelsDrawer';
import PickupAppointmentInboundParcelsDrawer from './PickupAppointmentInboundParcelsDrawer';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import {getUserInfo} from '../../../../utils/auth';
import { usePickupStartPartialGray } from '../../../../hooks/usePickupStartPartialGray';
import {packageUnitFilterOptions} from "../types/PickupAppointmentType";
import {formatTimestampToDateTime} from '../../../../utils/dateTimeUtils';
import { GET_PICKUP_ROUTES_BY_WAREHOUSE } from '../../pickup-task/graphql/queries/getPickupRoutes';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import CooperationWarehouseFormDrawer from '../../cooperation-warehouse/components/CooperationWarehouseFormDrawer';
import { GET_COOPERATION_WAREHOUSE_BY_ID, FindCooperationWarehouseByIdResponse } from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import { usePickupSiteV2Gray } from '../../../../hooks/usePickupSiteV2Gray';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import { useStationViewer } from '../../address-review-pool/hooks/useStationViewer';
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { Identifier, XYCoord } from 'dnd-core';

const DEBUG_PICKUP_APPOINTMENT_COLUMNS = false;


interface PickupAppointmentColumnsProps {
    appointmentData: PickupAppointmentType[];
    showAppointmentDetail: (record: PickupAppointmentType) => void;
    showTaskDetail?: (taskId: string) => void; // 添加显示任务详情的回调
    onSelectChange: (newSelectedRowKeys: React.Key[], newSelectedRows: PickupAppointmentType[]) => void;
    selectedRowKeys: React.Key[];
    loading: boolean;
    onRefresh?: () => void; // 添加刷新回调
    confirm?: number; // 0 pending, 1 confirmed
    customColumnOrder?: string[]; // 自定义列顺序
    scrollY?: number | string; // 表格滚动高度
    onDragSort?: (newData: PickupAppointmentType[]) => void; // 拖拽排序回调
    onManualSort?: (newData: PickupAppointmentType[]) => void; // 手动排序回调
    enableDragSort?: boolean; // 是否启用拖拽排序和站点顺序列，默认 false
    useSystemRouting?: boolean; // 是否使用系统排线，true 时隐藏揽收顺序列
}

const type = 'DraggableBodyRow';

interface DragItem {
    index: number;
    id: string;
    type: string;
}

interface DraggableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    index: number;
    moveRow: (dragIndex: number, hoverIndex: number) => void;
    'data-row-key'?: any;
    isSelected?: boolean; // 是否选中
}

const DraggableBodyRow: React.FC<DraggableRowProps> = React.memo(({ index, moveRow, className, style, isSelected, ...restProps }) => {
    const ref = useRef<HTMLTableRowElement>(null);

    //console.log(`🔵 DraggableBodyRow render - index: ${index}, isSelected: ${isSelected}, key: ${restProps['data-row-key']}`);

    // 使用 ref 来存储上一次的 collect 结果，避免不必要的重新渲染
    const dropCollectCache = useRef<{ handlerId: Identifier | null; isOver: boolean; isDraggingOver: boolean }>({
        handlerId: null,
        isOver: false,
        isDraggingOver: false
    });
    const dragCollectCache = useRef<{ isDragging: boolean; isOverAny: boolean }>({
        isDragging: false,
        isOverAny: false
    });

    // 添加节流机制，避免拖拽时频繁调用 moveRow
    const lastMoveTimeRef = useRef(0);
    const MOVE_THROTTLE_MS = 1; // 1ms 内只允许一次移动

    const [{ handlerId, isOver, isDraggingOver }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null; isOver: boolean; isDraggingOver: boolean }>({
        accept: type,
        collect: (monitor) => {
            const newHandlerId = monitor.getHandlerId();
            const newIsOver = monitor.isOver();
            const newIsDraggingOver = monitor.isOver() && monitor.canDrop();

            // 只有当值真正变化时才返回新对象
            if (
                dropCollectCache.current.handlerId !== newHandlerId ||
                dropCollectCache.current.isOver !== newIsOver ||
                dropCollectCache.current.isDraggingOver !== newIsDraggingOver
            ) {
                if (DEBUG_PICKUP_APPOINTMENT_COLUMNS) {
                    console.log(`🟡 useDrop collect CHANGED - index: ${index}, isOver: ${newIsOver}, isDraggingOver: ${newIsDraggingOver}`);
                }
                dropCollectCache.current = {
                    handlerId: newHandlerId,
                    isOver: newIsOver,
                    isDraggingOver: newIsDraggingOver
                };
            }

            return dropCollectCache.current;
        },
        // 只有选中的行才能接受拖放
        canDrop: () => isSelected === true,
        hover: (item: DragItem, monitor) => {
            if (!ref.current || !isSelected) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // 如果拖拽项和悬停项相同，不做任何处理
            if (dragIndex === hoverIndex) {
                return;
            }

            // 节流：避免频繁调用 moveRow
            const now = Date.now();
            if (now - lastMoveTimeRef.current < MOVE_THROTTLE_MS) {
                return;
            }
            lastMoveTimeRef.current = now;

            // 支持跨多行拖拽：直接移动到目标位置
            // 移除中线检查，允许跨行拖拽
            moveRow(dragIndex, hoverIndex);

            // 注意：这里需要修改 item.index，因为我们正在修改数组
            // 否则会导致位置计算错误
            item.index = hoverIndex;
        },
    });

    const [{ isDragging, isOverAny }, drag, dragPreview] = useDrag({
        type,
        item: { index, id: restProps['data-row-key'], type },
        collect: (monitor) => {
            const newIsDragging = monitor.isDragging();
            const newIsOverAny = monitor.isDragging() && monitor.getClientOffset() !== null;

            // 只有当值真正变化时才返回新对象
            if (
                dragCollectCache.current.isDragging !== newIsDragging ||
                dragCollectCache.current.isOverAny !== newIsOverAny
            ) {
                if (DEBUG_PICKUP_APPOINTMENT_COLUMNS) {
                    console.log(`🟠 useDrag collect CHANGED - index: ${index}, isDragging: ${newIsDragging}, isOverAny: ${newIsOverAny}`);
                }
                dragCollectCache.current = {
                    isDragging: newIsDragging,
                    isOverAny: newIsOverAny
                };
            }

            return dragCollectCache.current;
        },
        // 只有选中的行才能拖动
        canDrag: () => isSelected === true,
    });

    // 使用回调 ref 来绑定 drop 和 drag
    const callbackRef = useCallback((node: HTMLTableRowElement | null) => {
        if (DEBUG_PICKUP_APPOINTMENT_COLUMNS) {
            console.log(`🟢 callbackRef called - index: ${index}, isSelected: ${isSelected}, node: ${node ? 'exists' : 'null'}`);
        }
        if (node && isSelected) {
            // 绑定 drop
            drop(node);
            // 查找并绑定拖拽手柄
            const dragHandle = node.querySelector('.drag-handle');
            if (dragHandle) {
                drag(dragHandle as any);
            }
        }
        // 保存到 ref（不会触发重渲染）
        (ref as any).current = node;
    }, [isSelected, drop, drag, index]);

    return (
        <tr
            ref={callbackRef}
            data-handler-id={handlerId}
            className={className}
            style={{
                opacity: isDragging ? 0.8 : 1,
                backgroundColor: isDraggingOver ? '#e6f4ff' : 'transparent', // 被拖拽经过时浅蓝色背景
                fontWeight: isDragging && isOverAny ? 'bold' : 'normal', // 正在拖拽且移动时字体加粗
                fontSize: isDragging && isOverAny ? '18px' : (isDraggingOver ? '16px' : '14px'), // 被拖拽行18px，经过的行16px
                color: isDraggingOver ? '#1890ff' : 'inherit', // 经过的行文字变蓝色
                transition: 'background-color 0.2s ease, font-weight 0.1s ease, font-size 0.1s ease, color 0.2s ease',
                ...style
            }}
            {...restProps}
        />
    );
});

const PickupAppointmentColumns: React.FC<PickupAppointmentColumnsProps> = ({
                                                                               appointmentData,
                                                                               showAppointmentDetail,
                                                                               showTaskDetail,
                                                                               scrollY,
                                                                               onSelectChange,
                                                                               selectedRowKeys,
                                                                               loading,
                                                                               onRefresh,
                                                                               confirm,
                                                                               customColumnOrder,
                                                                               onDragSort,
                                                                               onManualSort,
                                                                               enableDragSort = false, // 默认不启用拖拽
                                                                               useSystemRouting = false, // 默认不使用系统排线
                                                                           }) => {
    // 渲染计数
    const renderCount = useRef(0);
    renderCount.current += 1;
    if (DEBUG_PICKUP_APPOINTMENT_COLUMNS) {
        console.log(`🔶 PickupAppointmentColumns render #${renderCount.current} - appointmentData: ${appointmentData.length}, selectedRowKeys: ${selectedRowKeys.length}`);
    }

    // 使用 ref 保存 selectedRowKeys，避免 components 重新创建
    const selectedRowKeysRef = useRef(selectedRowKeys);
    useEffect(() => {
        selectedRowKeysRef.current = selectedRowKeys;
    }, [selectedRowKeys]);

    // 取消预约弹窗状态
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [cancelingRecord, setCancelingRecord] = useState<PickupAppointmentType | null>(null);
    const [cancelVoucherS3Keys, setCancelVoucherS3Keys] = useState<string[]>([]);
    const [cancelVoucherError, setCancelVoucherError] = useState(false);

    // 添加明细抽屉状态
    const [parcelsDrawerVisible, setParcelsDrawerVisible] = useState(false);
    // 「实际入库包裹数量」明细抽屉：与上面那个是两个口径，共用一份 selectedAppointmentId
    const [inboundParcelsDrawerVisible, setInboundParcelsDrawerVisible] = useState(false);
    // 上门扫描单号 → 包裹详情抽屉（抽屉只认 orderId）
    const [scanParcelDetailVisible, setScanParcelDetailVisible] = useState(false);
    const [scanParcelOrderId, setScanParcelOrderId] = useState<string>('');
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<number | null>(null);
    const [selectedAppointmentNumber, setSelectedAppointmentNumber] = useState<string>('');
    // 添加合作仓库详情抽屉状态
    const [cooperationWarehouseDrawerVisible, setCooperationWarehouseDrawerVisible] = useState(false);
    const [selectedCooperationWarehouse, setSelectedCooperationWarehouse] = useState<any>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');
    const [originalCooperationWarehouseName, setOriginalCooperationWarehouseName] = useState<string>('');

    // 移动行的处理函数 - 直接调用父组件的回调
    const moveRow = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            if (DEBUG_PICKUP_APPOINTMENT_COLUMNS) {
                console.log(`🔴 moveRow called - dragIndex: ${dragIndex}, hoverIndex: ${hoverIndex}`);
            }
            if (dragIndex === hoverIndex || !onManualSort) {
                return;
            }

            const newData = [...appointmentData];
            const [removed] = newData.splice(dragIndex, 1);
            newData.splice(hoverIndex, 0, removed);
            if (DEBUG_PICKUP_APPOINTMENT_COLUMNS) {
                console.log(`🔴 moveRow calling onManualSort with ${newData.length} items`);
            }
            onManualSort(newData);
        },
        [appointmentData, onManualSort],
    );

    // 获取当前仓库信息
    const { currentWarehouse } = useWarehouseContext();
    // 灰度：未命中的 hub 不给「实际入库包裹数量」链接，保持改造前的纯文本
    const { startPartialEnabled } = usePickupStartPartialGray();
    const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;

    // 揽收站点 2.0 灰度：灰度 hub 下点站点走 2.0 编辑弹窗（建联状态锁死不可改，与站点管理页一致），非灰度沿用老 1.0 组件
    const { express2Enabled } = usePickupSiteV2Gray();
    const isV2 = express2Enabled(warehouseId);
    const { openEditStation, viewerDrawer } = useStationViewer({ onSaved: () => onRefresh?.() });

    // 获取路线数据
    const { data: routesData, loading: routesLoading } = useQuery(GET_PICKUP_ROUTES_BY_WAREHOUSE, {
        variables: { warehouseId: warehouseId ? warehouseId : 0 },
        skip: !warehouseId,
    });
    // 国际化
    const {t} = useTranslation();
    const { getPickupAppointmentStatusText, getPickupAppointmentSourceText} = useStatusInternationalization();
    // 车型列：后端已按「单上有值用单上的、为空回退站点当日排期」算好 vehicleTypeId，前端只负责译名
    const { vehicleTypes } = useVehicleTypes();
    const vehicleTypeNameMap = useMemo(
        () => new Map((vehicleTypes || []).map(vt => [vt.id, vt.nameEn])),
        [vehicleTypes]
    );
    // 查询合作仓库详情
    const { refetch: refetchCooperationWarehouseDetail } = useQuery<FindCooperationWarehouseByIdResponse>(
        GET_COOPERATION_WAREHOUSE_BY_ID,
        {
            variables: { id: null },
            skip: true,
        }
    );

    // 取消预约的 mutation
    const [cancelAppointment, {loading: cancelLoading}] = useMutation<CancelPickupAppointmentResponse>(
        CANCEL_PICKUP_APPOINTMENT,
        {
            onCompleted: (data) => {
                setCancelModalVisible(false);
                setCancelVoucherS3Keys([]);
                if (data.cancelPickupAppointment) {
                    message.success(t('pickup.appointment.cancelSuccess'));
                    onRefresh?.(); // 刷新数据
                } else {
                    message.error(t('pickup.appointment.cancelFailed'));
                }
            },
            onError: (error) => {
                setCancelModalVisible(false);
                setCancelVoucherS3Keys([]);
                const msg = error.message === 'cancel_voucher_required'
                    ? t('pickup.appointment.cancelVoucherRequired')
                    : t('pickup.appointment.cancelError', { message: error.message });
                message.error(msg);
            }
        }
    );

    // 更新备注的 mutation
    const [updateAppointmentNotes] = useMutation<UpdateAppointmentNotesResponse>(
        UPDATE_APPOINTMENT_NOTES,
        {
            onCompleted: () => {
                message.success(t('pickup.appointment.messages.notesUpdatedSuccessfully') as string);
            },
            onError: (error) => {
                message.error(t('pickup.appointment.messages.notesUpdateFailed') as string + ': ' + error.message);
            }
        }
    );

    // 编辑中的备注状态
    const [editingNotes, setEditingNotes] = useState<{ [key: number]: string }>({});
    // 跟踪每个记录的保存状态
    const [savingRecordIds, setSavingRecordIds] = useState<Set<number>>(new Set());
    // 跟踪已保存的记录，保存后按钮不显示
    const [savedRecordIds, setSavedRecordIds] = useState<Set<number>>(new Set());
    // 记录每个记录的原始 notes 值，用于比较是否有变化
    const [originalNotes, setOriginalNotes] = useState<{ [key: number]: string }>({});

    // 当数据刷新时，同步 originalNotes 和 editingNotes
    useEffect(() => {
        setOriginalNotes(prev => {
            const newOriginalNotes = { ...prev };
            let hasChanges = false;

            appointmentData.forEach(record => {
                const serverNotes = record.notes ?? '';
                const currentOriginalNotes = prev[record.id] ?? '';

                // 如果 originalNotes 中没有该记录，或者值不同，则同步更新
                if (currentOriginalNotes !== serverNotes) {
                    newOriginalNotes[record.id] = serverNotes;
                    hasChanges = true;
                }
            });

            // 同步更新 editingNotes：只有当 editingNotes 中的值等于旧的 originalNotes 时，才更新
            // 这样可以确保保存后的值能正确显示，同时不会覆盖用户正在编辑的内容
            if (hasChanges) {
                setEditingNotes(prevEditing => {
                    const newEditingNotes = { ...prevEditing };
                    let editingHasChanges = false;

                    appointmentData.forEach(record => {
                        const serverNotes = record.notes ?? '';
                        const currentEditingValue = prevEditing[record.id];
                        const oldOriginalNotes = prev[record.id] ?? '';

                        // 如果 editingNotes 中有该记录，且值等于旧的 originalNotes，则同步更新
                        // 这样可以确保保存后的值能正确显示
                        if (currentEditingValue !== undefined && currentEditingValue === oldOriginalNotes) {
                            newEditingNotes[record.id] = serverNotes;
                            editingHasChanges = true;
                        }
                    });

                    return editingHasChanges ? newEditingNotes : prevEditing;
                });
            }

            return hasChanges ? newOriginalNotes : prev;
        });
    }, [appointmentData]);

    // 处理备注保存
    const handleSaveNotes = async (record: PickupAppointmentType) => {
        const newNotes = editingNotes[record.id] ?? record.notes ?? '';

        // 设置当前记录的保存状态
        setSavingRecordIds(prev => new Set(prev).add(record.id));

        try {
            await updateAppointmentNotes({
                variables: {
                    appointmentId: record.id,
                    notes: newNotes || null
                }
            });

            // 保存成功后，更新编辑状态为保存的值，这样 input 会显示保存的值
            setEditingNotes(prev => ({
                ...prev,
                [record.id]: newNotes || ''
            }));
            // 更新原始值为保存的值，用于下次编辑时比较
            setOriginalNotes(prev => ({
                ...prev,
                [record.id]: newNotes || ''
            }));
            // 标记为已保存，保存后按钮不显示
            setSavedRecordIds(prev => new Set(prev).add(record.id));
        } catch (error) {
            console.error('Failed to update notes:', error);
        } finally {
            // 清除当前记录的保存状态
            setSavingRecordIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(record.id);
                return newSet;
            });
        }
    };

    // 处理查看实际入库包裹明细
    const handleViewScanParcelDetail = (record: PickupAppointmentType, event: React.MouseEvent) => {
        event.stopPropagation();
        if (!record.scanOrderId) return;
        setScanParcelOrderId(record.scanOrderId.toString());
        setScanParcelDetailVisible(true);
    };

    const handleViewInboundParcels = (record: PickupAppointmentType, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedAppointmentId(record.id);
        setSelectedAppointmentNumber(record.appointmentNumber);
        setInboundParcelsDrawerVisible(true);
    };

    // 处理包裹抽屉关闭
    const handleParcelsDrawerClose = () => {
        setParcelsDrawerVisible(false);
    };

    // 处理取消预约
    const handleCancelAppointment = (record: PickupAppointmentType, event: React.MouseEvent) => {
        // 阻止事件冒泡，避免触发其他点击事件
        event.stopPropagation();
        event.preventDefault();
        setCancelingRecord(record);
        setCancelVoucherS3Keys([]);
        setCancelVoucherError(false);
        setCancelModalVisible(true);
    };

    // 判断是否可以取消
    const isCancelable = (status?: string) => {
        if (!status) return false;
        const cancelableStatuses = ['CREATED', 'DISPATCHING', 'DISPATCHED', 'PICKING_UP'];
        return cancelableStatuses.includes(status);
    };

    // 根据邮编查找路线名称
    const getRouteNameByZipcode = (zipcode: string): string => {
        if (!routesData?.getPickupRoutesByWarehouseId) return '-';

        for (const route of routesData.getPickupRoutesByWarehouseId) {
            try {
                const zipcodes = JSON.parse(route.zipcodesListJson);
                if (Array.isArray(zipcodes) && zipcodes.includes(zipcode)) {
                    return route.routeName;
                }
            } catch (e) {
                console.warn('Failed to parse zipcodes for route:', route.id);
            }
        }
        return '-';
    };

    // 处理查看包裹明细
    const handleViewParcels = (record: PickupAppointmentType, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedAppointmentId(record.id);
        setSelectedAppointmentNumber(record.appointmentNumber);
        setParcelsDrawerVisible(true);
    };

    // 处理查看合作仓库详情
    const showPickupSiteDetail = async (cooperationWarehouseId: number) => {
        if (!cooperationWarehouseId) {
            message.warning('No cooperation warehouse ID available');
            return;
        }

        // 灰度 hub：走 2.0 站点编辑弹窗（可编辑，建联状态锁死），保存后 onRefresh 刷新列表
        if (isV2 && warehouseId) {
            openEditStation(cooperationWarehouseId, warehouseId);
            return;
        }

        // 1. 立即打开抽屉，显示加载动画
        setSelectedCooperationWarehouse(null);
        setDrawerMode('view');
        setCooperationWarehouseDrawerVisible(true);

        try {
            // 2. 调用后端查询
            const result = await refetchCooperationWarehouseDetail({ id: cooperationWarehouseId });
            const warehouseDetail = result.data?.getCooperationWarehouseById;

            if (warehouseDetail) {
                // 3. 设置数据，结束加载动画
                setSelectedCooperationWarehouse(warehouseDetail);
                // 保存原始的 cooperationWarehouseName 用于后续比较
                setOriginalCooperationWarehouseName(warehouseDetail.name || '');
            } else {
                message.error('Cooperation warehouse detail not found');
                setCooperationWarehouseDrawerVisible(false);
            }
        } catch (error) {
            console.error('Failed to load cooperation warehouse details:', error);
            message.error('Failed to load cooperation warehouse details');
            setCooperationWarehouseDrawerVisible(false);
        }
    };

    // 处理合作仓库抽屉提交
    const handleCooperationWarehouseDrawerSubmit = async (values: any) => {
        // 检查是否是从展示模式切换到编辑模式
        if (values._switchToEdit) {
            setDrawerMode('edit');
            return; // 不关闭抽屉，继续编辑
        }

        // 如果是编辑模式，检查 cooperationWarehouseName 是否有变更
        if (drawerMode === 'edit') {
            const formData = values.formData;
            if (formData) {
                const newCooperationWarehouseName = formData.name || '';
                // 如果cooperationWarehouseName名称不变，不需要refresh页面,避免不必要的网络请求--因为页面只展示了cooperationWarehouseName名称
                if (originalCooperationWarehouseName !== newCooperationWarehouseName) {
                    onRefresh?.();
                }
            }
        }

        setCooperationWarehouseDrawerVisible(false);
    };

    const allColumns: ColumnsType<PickupAppointmentType> = [
        // 站点顺序列 - 仅在启用拖拽排序且不使用系统排线时显示
        ...(enableDragSort && !useSystemRouting ? [{
            title: t('pickup.appointment.columns.siteOrder'),
            key: 'index',
            width: 80,
            align: 'left' as const,
            fixed: 'left' as const,
            render: (_: any, record: PickupAppointmentType, index: number) => {
                const isSelected = selectedRowKeys.includes(record.id);
                // 只有选中时才显示内容
                if (!isSelected) {
                    return null;
                }
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <div
                            className="drag-handle"
                            style={{ display: 'flex', alignItems: 'center', cursor: 'move', userSelect: 'none' }}
                        >
                            <svg
                                viewBox="0 0 1024 1024"
                                version="1.1"
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                style={{ display: 'block' }}
                            >
                                <path d="M399.786667 143.786667L146.346667 397.226667c-13.226667 13.226667-34.986667 13.226667-48.213334 0-13.226667-13.226667-13.226667-34.986667 0-48.213334l253.44-253.44c13.226667-13.226667 34.986667-13.226667 48.213334 0 13.226667 13.226667 13.226667 34.56 0 48.213334z" fill="#4C4C4C"></path>
                                <path d="M375.466667 938.666667c-18.773333 0-34.133333-15.36-34.133334-34.133334V119.466667c0-18.773333 15.36-34.133333 34.133334-34.133334s34.133333 15.36 34.133333 34.133334v785.066666c0 18.773333-15.36 34.133333-34.133333 34.133334zM624.213333 880.213333l253.44-253.44c13.226667-13.226667 34.986667-13.226667 48.213334 0 13.226667 13.226667 13.226667 34.986667 0 48.213334l-253.44 253.44c-13.226667 13.226667-34.986667 13.226667-48.213334 0-13.226667-13.226667-13.226667-34.56 0-48.213334z" fill="#4C4C4C"></path>
                                <path d="M648.533333 85.333333c18.773333 0 34.133333 15.36 34.133334 34.133334v785.066666c0 18.773333-15.36 34.133333-34.133334 34.133334s-34.133333-15.36-34.133333-34.133334V119.466667c0-18.773333 15.36-34.133333 34.133333-34.133334z" fill="#4C4C4C"></path>
                            </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                    </div>
                );
            },
        }] : []),
        {
            title: t('pickup.appointment.columns.appointmentNumber'),
            dataIndex: 'appointmentNumber',
            key: 'appointmentNumber',
            width: 200,
            align: 'left',
            fixed: 'left' ,
            render: (_: string, record: PickupAppointmentType) => (
                <Button
                    type="link"
                    onClick={() => showAppointmentDetail(record)}
                    className="p-0 h-auto text-blue-600 hover:text-blue-800"
                >
                    <span className="text-sm">{record.appointmentNumber}</span>
                </Button>
            ),
        },
        // Remark 列 - 只在 pending confirmation appointment 中显示
        ...(confirm === 0 ? [{
            title: t('pickup.appointment.columns.remark'),
            dataIndex: 'notes',
            key: 'remark',
            width: 200,
            align: 'left' as const,
            render: (notes: string | undefined, record: PickupAppointmentType) => {
                const isEditing = editingNotes.hasOwnProperty(record.id);
                // 获取原始值：优先使用 originalNotes，如果不存在则使用 record.notes
                const baseOriginalNotes = originalNotes[record.id] ?? notes ?? '';
                // 获取当前显示的值：如果正在编辑，使用 editingNotes，否则使用原始值
                const currentValue = isEditing ? (editingNotes[record.id] ?? baseOriginalNotes) : baseOriginalNotes;
                const isSaved = savedRecordIds.has(record.id);
                // 如果已保存，不显示按钮；否则判断是否有变化
                // 使用 originalNotes 进行比较，确保比较的准确性
                const editingValue = editingNotes[record.id] ?? '';
                const hasChanges = !isSaved && isEditing && editingValue !== baseOriginalNotes;

                return (
                    <div style={{ position: 'relative', width: '100%' }}>
                        <Input.TextArea
                            value={currentValue}
                            onChange={(e) => {
                                setEditingNotes(prev => ({
                                    ...prev,
                                    [record.id]: e.target.value
                                }));
                                // 开始编辑时，如果 originalNotes 中没有该记录，初始化它
                                if (!originalNotes.hasOwnProperty(record.id)) {
                                    setOriginalNotes(prev => ({
                                        ...prev,
                                        [record.id]: notes ?? ''
                                    }));
                                }
                                // 开始编辑时，清除已保存标记，这样按钮能重新显示
                                if (savedRecordIds.has(record.id)) {
                                    setSavedRecordIds(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(record.id);
                                        return newSet;
                                    });
                                }
                            }}
                            onBlur={() => {
                                // 使用函数式更新确保获取最新的状态值
                                setEditingNotes(prev => {
                                    const currentEditingValue = prev[record.id] ?? '';
                                    // 使用 originalNotes 进行比较
                                    const currentBaseOriginalNotes = originalNotes[record.id] ?? notes ?? '';
                                    const currentHasChanges = currentEditingValue !== currentBaseOriginalNotes;

                                    // 如果没有变化，清除编辑状态
                                    if (!currentHasChanges) {
                                        const newState = { ...prev };
                                        delete newState[record.id];
                                        return newState;
                                    }
                                    // 如果有变化，保持编辑状态
                                    return prev;
                                });
                            }}
                            placeholder={t('pickup.appointment.placeholders.enterRemark') as string}
                            disabled={savingRecordIds.has(record.id)}
                            autoSize={{ minRows: 1, maxRows: 6 }}
                            style={{
                                width: '100%',
                                resize: 'none'
                            }}
                        />
                        {hasChanges && (
                            <Button
                                type="text"
                                icon={<CheckOutlined />}
                                size="small"
                                loading={savingRecordIds.has(record.id)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSaveNotes(record);
                                }}
                                style={{
                                    position: 'absolute',
                                    bottom: '4px',
                                    right: '4px',
                                    padding: 0,
                                    height: 'auto',
                                    color: '#722ed1',
                                    zIndex: 1
                                }}
                            />
                        )}
                    </div>
                );
            },
        }] : []),
        {
            title: t('pickup.appointment.columns.hub'),
            dataIndex: 'warehouseName',
            key: 'warehouseName',
            width: 120,
            align: 'left',
            render: (warehouseName: string) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{warehouseName}</span>
            ),
        },
        {
            title: 'warehouseLatitude',
            dataIndex: 'warehouseLatitude',
            key: 'warehouseLatitude',
            width: 120,
            align: 'left',
            hidden: true,
        },
        {
            title: 'warehouseLongitude',
            dataIndex: 'warehouseLongitude',
            key: 'warehouseLongitude',
            width: 120,
            align: 'left',
            hidden: true,
        },
        {
            title: 'cooperationWarehouseLatitude',
            dataIndex: 'cooperationWarehouseLatitude',
            key: 'cooperationWarehouseLatitude',
            width: 120,
            align: 'left',
            hidden: true,
        },
        {
            title: 'cooperationWarehouseLongitude',
            dataIndex: 'cooperationWarehouseLongitude',
            key: 'cooperationWarehouseLongitude',
            width: 120,
            align: 'left',
            hidden: true,
        },
        {
            title: t('pickup.appointment.columns.pickupSiteId'),
            dataIndex: 'pickupSiteId',
            key: 'pickupSiteId',
            width: 150,
            align: 'left',
            render: (pickupSiteId: number) => (
                <span className="text-sm">{pickupSiteId || '--'}</span>
            )
        },
        {
            title: t('pickup.appointment.columns.pickupSite'),
            dataIndex: 'cooperationWarehouseName',
            width: 150,
            ellipsis: true,
            align: 'left',
            render: (cooperationWarehouseName: string, record: PickupAppointmentType) => (
                <Button
                    type="link"
                    style={{ padding: 0, maxWidth: '100%' }}
                    onClick={() => record.cooperationWarehouseId && showPickupSiteDetail(record.cooperationWarehouseId)}
                    disabled={!record.cooperationWarehouseId}
                >
                    <span style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                    }}>
                        {cooperationWarehouseName || '--'}
                    </span>
                </Button>
            )
        },
        {
            title: t('pickup.appointment.columns.contactPhone'),
            key: 'contactPhone',
            width: 150,
            align: 'left',
            render: (_: any, record: PickupAppointmentType) => {
                if (record.contactPhone) {
                    const countryCode = record.countryCode || '';
                    const extension = record.ext ? `-${record.ext}` : '';
                    return `+${countryCode} ${record.contactPhone}${extension}`;
                }
                return '-';
            }
        },
        {
            title: t('pickup.appointment.columns.customerName'),
            dataIndex: 'channelName',
            key: 'channelName',
            width: 150,
            align: 'left',
            render: (name: string) => (
                <span className="text-sm">{name}</span>
            ),
        },
        {
            title: t('pickup.appointment.columns.address'),
            key: 'address',
            width: 200,
            align: 'left',
            sorter: (a: PickupAppointmentType, b: PickupAppointmentType) => {
                const addressA = `${a.addressLine1 || ''}${a.addressLine2 || ''}`.toLowerCase();
                const addressB = `${b.addressLine1 || ''}${b.addressLine2 || ''}`.toLowerCase();
                return addressA.localeCompare(addressB);
            },
            showSorterTooltip: {title: 'Click to sort by address'},
            render: (_, record: PickupAppointmentType) => (
                <div className="text-sm">
                    <div style={{whiteSpace: 'pre-wrap'}}>
                        {record.addressLine1}
                        {record.addressLine2 ? `,${record.addressLine2}` : ''}
                    </div>
                </div>
            ),
        },
        {
            title: t('pickup.appointment.filters.source'),
            dataIndex: 'source',
            key: 'source',
            width: 150,
            align: 'left',
            render: (_, record: PickupAppointmentType) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{getPickupAppointmentSourceText(record.source)}</span>
            ),
        },
        {
            title: t('pickup.appointment.filters.isNew'),
            dataIndex: 'isNew',
            key: 'isNew',
            width: 80,
            align: 'left',
            render: (isNew: number | boolean | undefined | null) => {
                const isYes = isNew === 1 || isNew === true;
                const isNo = isNew === 0 || isNew === false;
                const text = isYes ? t('enum.yesorNoStatus.yes') : isNo ? t('enum.yesorNoStatus.no') : '--';
                return <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('pickup.appointment.filters.continuousFailure'),
            dataIndex: 'continuousFailure',
            key: 'continuousFailure',
            width: 120,
            align: 'left',
            render: (continuousFailure: number | boolean | undefined | null) => {
                const isYes = continuousFailure === 1 || continuousFailure === true;
                const isNo = continuousFailure === 0 || continuousFailure === false;
                const text = isYes ? t('enum.yesorNoStatus.yes') : isNo ? t('enum.yesorNoStatus.no') : '--';
                return <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('pickup.appointment.columns.route'),
            key: 'route',
            width: 120,
            align: 'left',
            render: (_: any, record: PickupAppointmentType) => {
                const routeName = getRouteNameByZipcode(record.zipcode);
                return <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{routeName}</span>;
            },
        },
        {
            title: t('pickup.appointment.columns.zipcode'),
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 100,
            align: 'left',
            sorter: (a: PickupAppointmentType, b: PickupAppointmentType) => {
                const zipcodeA = a.zipcode || '';
                const zipcodeB = b.zipcode || '';
                return zipcodeA.localeCompare(zipcodeB);
            },
            showSorterTooltip: {title: 'Click to sort by zipcode'},
            render: (zipcode: string) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{zipcode}</span>
            ),
        },
        {
            title: t('pickup.appointment.columns.vehicleType'),
            dataIndex: 'vehicleTypeId',
            key: 'vehicleType',
            width: 130,
            align: 'left',
            render: (vehicleTypeId?: number) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>
                    {(vehicleTypeId != null && vehicleTypeNameMap.get(vehicleTypeId)) || '--'}
                </span>
            ),
        },
        {
            title: t('pickup.appointment.columns.expectedTime'),
            key: 'expectedPickupTime',
            width: 150,
            align: 'left',
            render: (_, record: PickupAppointmentType) => (
                <div className="text-sm">
                    <div>{record.expectedPickupDate}</div>
                    <div className="text-gray-500">
                        {record.expectedPickupTimeStart}-{record.expectedPickupTimeEnd}
                    </div>
                </div>
            ),
        },
        {
            title: t('pickup.appointment.columns.timezone'),
            dataIndex: 'timezone',
            key: 'timezone',
            width: 80,
            align: 'left',
            render: (timezone: string) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{timezone || '--'}</span>
            ),
        },
        {
            title: t('pickup.appointment.columns.expectedCount'),
            dataIndex: 'expectedParcelCount',
            key: 'expectedParcelCount',
            width: 140,
            align: 'left',
            render: (expected: number) => (
                <span className="text-sm">{expected || '--'}</span>
            ),
        },
        {
            title: t('pickup.appointment.columns.expectedUnit'),
            dataIndex: 'expectedParcelUnit',
            key: 'expectedParcelUnit',
            width: 140,
            align: 'left',
            render: (unit) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>
                    {packageUnitFilterOptions.find(option => option.value === unit)?.label || unit || '--'}
                </span>
            )
        },
        {
            title: t('pickup.appointment.columns.actualCount'),
            dataIndex: 'actualParcelCount',
            key: 'actualParcelCount',
            width: 140,
            align: 'left',
            render: (actual: number) => (
                <span className="text-sm">{actual || '--'}</span>
            ),
        },
        {
            title: t('pickup.appointment.columns.actualUnit'),
            dataIndex: 'actualParcelUnit',
            key: 'actualParcelUnit',
            width: 140,
            align: 'left',
            render: (unit: string) => {
                return (
                    <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>
                        {packageUnitFilterOptions.find(option => option.value === unit)?.label ?? unit ?? '--'}
                    </span>
                );
            }
        },
        {
            title: t('pickup.appointment.columns.parcels'),
            key: 'details',
            width: 80,
            align: 'left',
            render: (_: any, record: PickupAppointmentType) => {
                // 取挂载运单数而非预期数量：本列点开是该预约单的运单明细抽屉，
                // 数字必须与抽屉里的条数一致，不能跟着手动编辑的预期数量走
                const count = record.pickupOrderTotalCount || 0;

                return (
                    <Button
                        type="link"
                        onClick={(e) => handleViewParcels(record, e)}
                        className={`!p-0 !h-auto font-medium ${
                            count > 0 ? 'text-purple-600' : 'text-gray-400'
                        }`}
                    >
                        {count}
                    </Button>
                );
            },
        },
        {
            title: t('pickup.appointment.columns.actualScanCount'),
            dataIndex: 'actualScanParcelCount',
            key: 'actualScanParcelCount',
            width: 140,
            align: 'left',
            render: (actual: number, record: PickupAppointmentType) => {
                const count = actual || 0;
                // 为 0 时不给链接：点开必然是空表，白白多一次查询。
                // 非灰度 hub 同样只给纯文本——该 hub 的明细口径尚未放开
                if (count === 0 || !startPartialEnabled(currentWarehouse?.id)) {
                    return <span className="text-sm">--</span>;
                }
                return (
                    <Button
                        type="link"
                        onClick={(e) => handleViewInboundParcels(record, e)}
                        className="!p-0 !h-auto font-medium text-purple-600"
                    >
                        {count}
                    </Button>
                );
            },
        },
        {
            title: t('pickup.appointment.columns.scanTrackingNumber'),
            dataIndex: 'scanTrackingNumber',
            key: 'scanTrackingNumber',
            width: 200,
            align: 'left',
            render: (scanTrackingNumber: string | null | undefined, record: PickupAppointmentType) => {
                if (!scanTrackingNumber) {
                    return <span className="text-sm">--</span>;
                }
                // 没有订单 ID 时打不开包裹详情，只展示单号
                if (!record.scanOrderId) {
                    return <span className="text-sm">{scanTrackingNumber}</span>;
                }
                return (
                    <Button
                        type="link"
                        onClick={(e) => handleViewScanParcelDetail(record, e)}
                        className="!p-0 !h-auto font-medium text-purple-600"
                    >
                        {scanTrackingNumber}
                    </Button>
                );
            },
        },
        {
            title: t('pickup.appointment.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            align: 'left',
            render: (status: string) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{getPickupAppointmentStatusText(status)}</span>
            ),
        },
        {
            title: t('pickup.appointment.columns.taskNumber'),
            dataIndex: 'taskNumber',
            key: 'taskNumber',
            width: 150,
            align: 'left',
            render: (taskNumber: string, record: PickupAppointmentType) => {
                // 检查是否有有效的任务号和任务ID
                const hasValidTask = taskNumber && taskNumber.trim() !== '' && record.taskId && record.taskId > 0;

                // 如果有有效任务且有显示任务详情的回调函数，则显示为可点击的链接
                if (hasValidTask && showTaskDetail) {
                    return (
                        <Button
                            type="link"
                            onClick={() => showTaskDetail(record.taskId!.toString())}
                            className="p-0 h-auto text-blue-600 hover:text-blue-800"
                        >
                            <span className="text-sm">{taskNumber}</span>
                        </Button>
                    );
                }
                // 否则显示为普通文本
                return <span className="text-sm">{taskNumber || '--'}</span>;
            },
        },
        {
            title: t('pickup.appointment.columns.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            align: 'left',
            render: (updatedAt: string) => (
                <span className="text-sm">{updatedAt ? formatTimestampToDateTime(updatedAt) : '--'}</span>
            ),
        },
        {
            title: t('central.sites.details.actions'),
            key: 'actions',
            width: 80,
            fixed: 'right',
            align: 'left',
            render: (_, record: PickupAppointmentType) => {
                // 判断是否允许取消
                const canCancel = isCancelable(record.status);

                return (
                    <Button
                        type="link"
                        size="small"
                        danger
                        loading={cancelLoading}
                        disabled={!canCancel}
                        onClick={(event) => handleCancelAppointment(record, event)}
                        className="p-0 h-auto text-red-600 hover:text-red-800"
                    >
                        {t('pickup.appointment.columns.cancel')}
                    </Button>
                );
            }
        },
    ];

    // 如果提供了自定义列顺序，则重新排序列
    const columns = useMemo(() => customColumnOrder
        ? customColumnOrder.map(key => allColumns.find(col => col.key === key)).filter(Boolean) as ColumnsType<PickupAppointmentType>
        : allColumns, [customColumnOrder, allColumns]);

    //在Confirmed Appointment 使用的选择配置
    const rowSelection = useMemo(() => ({
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: PickupAppointmentType) => ({
            disabled: record.status !== 'CREATED',
            onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
            },
        }),
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
        ],
        columnWidth: 40,
    }), [selectedRowKeys, onSelectChange]);

    // 仅在 Pending Confirmation Appointment 使用的选择配置
    const rowSelectionPedding = useMemo(() => ({
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: PickupAppointmentType) => ({
            disabled: record.status == 'CANCELLED',
            onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
            },
        }),
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
        ],
        columnWidth: 40,
    }), [selectedRowKeys, onSelectChange]);


    // 使用 ref 保存 moveRow，确保完全稳定
    const moveRowRef = useRef(moveRow);
    useEffect(() => {
        moveRowRef.current = moveRow;
    }, [moveRow]);

    // 配置拖拽的组件 - 完全稳定，不依赖任何动态值
    // 所有动态值通过 ref 在运行时获取
    const components = useMemo(() => {
        if (DEBUG_PICKUP_APPOINTMENT_COLUMNS) {
            console.log(`🟣 components useMemo recalculated - THIS SHOULD ONLY HAPPEN ONCE`);
        }
        return {
            body: {
                row: (props: any) => {
                    // 在运行时从 ref 获取最新的值，避免依赖导致重新创建
                    const isSelected = selectedRowKeysRef.current.includes(props['data-row-key']);
                    return <DraggableBodyRow {...props} moveRow={moveRowRef.current} isSelected={isSelected} />;
                },
            },
        };
    }, []); // 空依赖数组 - 只创建一次

    // 表格内容
    const tableContent = (
        <>
            {enableDragSort && !useSystemRouting && (
                <style>
                    {`
                        /* 拖拽边界指示线 */
                        .drop-over-downward td {
                            border-bottom: 2px dashed #1890ff !important;
                        }
                        .drop-over-upward td {
                            border-top: 2px dashed #1890ff !important;
                        }
                        .ant-table-tbody > tr.drop-over-downward > td {
                            border-bottom: 2px dashed #1890ff !important;
                        }
                        .ant-table-tbody > tr.drop-over-upward > td {
                            border-top: 2px dashed #1890ff !important;
                        }

                        /* 拖拽中的行样式增强 (被拖拽的行 - 18px) */
                        .ant-table-tbody > tr[style*="font-size: 18px"] {
                            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
                            border: 2px solid #1890ff;
                            z-index: 1000;
                            position: relative;
                        }

                        /* 悬停目标行的浅蓝色背景和蓝色字体 (经过的行 - 16px) */
                        .ant-table-tbody > tr[style*="background-color: rgb(230, 244, 255)"] {
                            box-shadow: 0 3px 10px rgba(24, 144, 255, 0.25);
                            border: 1px solid #91d5ff;
                        }

                        /* 确保拖拽时字体变化平滑 */
                        .ant-table-tbody > tr {
                            transition: all 0.1s ease;
                        }
                    `}
                </style>
            )}
            <Table
                rowSelection={confirm === 1 ?  rowSelection : rowSelectionPedding}
                columns={columns}
                dataSource={appointmentData}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{x: 2200, ...(scrollY ? {y: scrollY} : {})}}
                virtual={!!scrollY && !(enableDragSort && !useSystemRouting)}
                size="small"
                sticky
                components={enableDragSort && !useSystemRouting ? components : undefined}
                onRow={enableDragSort && !useSystemRouting ? (record, index) => {
                    const isSelected = selectedRowKeys.includes(record.id);
                    const attr = {
                        index,
                        moveRow,
                        isSelected,
                    };
                    return attr as React.HTMLAttributes<any>;
                } : undefined}
            />

            {/* 包裹明细抽屉 */}
            {selectedAppointmentId && (
                <PickupAppointmentParcelsDrawer
                    visible={parcelsDrawerVisible}
                    onClose={handleParcelsDrawerClose}
                    appointmentId={selectedAppointmentId}
                    appointmentNumber={selectedAppointmentNumber}
                    initialStatus={appointmentData.find(app => app.id === selectedAppointmentId)?.status}
                />
            )}

            {/* 实际入库包裹明细抽屉（只读） */}
            {selectedAppointmentId && (
                <PickupAppointmentInboundParcelsDrawer
                    visible={inboundParcelsDrawerVisible}
                    onClose={() => setInboundParcelsDrawerVisible(false)}
                    appointmentId={selectedAppointmentId}
                    appointmentNumber={selectedAppointmentNumber}
                />
            )}

            {/* 上门扫描单号 → 包裹详情 */}
            {scanParcelDetailVisible && (
                <CentralParcelDetailDrawer
                    visible={scanParcelDetailVisible}
                    onClose={() => setScanParcelDetailVisible(false)}
                    orderId={scanParcelOrderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
            )}

            {/* 合作仓库详情抽屉 */}
            <CooperationWarehouseFormDrawer
                visible={cooperationWarehouseDrawerVisible}
                onClose={() => setCooperationWarehouseDrawerVisible(false)}
                onSubmit={handleCooperationWarehouseDrawerSubmit}
                initialValues={selectedCooperationWarehouse || undefined}
                mode={drawerMode}
                statusOptions={[
                    { label: t('enum.cooperationWarehouseStatus.active'), value: 1 },
                    { label: t('enum.cooperationWarehouseStatus.suspended'), value: 0 },
                    { label: t('enum.cooperationWarehouseStatus.pending'), value: 2 },
                ]}
            />

            {/* 揽收站点 2.0：灰度 hub 的站点查看/编辑走 2.0 弹窗（建联状态锁死） */}
            {viewerDrawer}

            {/* 取消预约弹窗（支持上传取消凭证） */}
            <Modal
                title={t('pickup.appointment.cancelTitle')}
                open={cancelModalVisible}
                okText={t('pickup.appointment.buttons.confirmCancellation')}
                cancelText={t('components.button.cancel')}
                confirmLoading={cancelLoading}
                onOk={() => {
                    if (!cancelingRecord) return;
                    // if (cancelVoucherS3Keys.length === 0) {
                    //     setCancelVoucherError(true);
                    //     return;
                    // }
                    cancelAppointment({
                        variables: {
                            id: cancelingRecord.id,
                            cancelVoucherS3Keys: cancelVoucherS3Keys
                        }
                    });
                }}
                onCancel={() => {
                    if (!cancelLoading) {
                        setCancelModalVisible(false);
                        setCancelVoucherS3Keys([]);
                        setCancelVoucherError(false);
                    }
                }}
            >
                <div className="mt-3">
                    <ImageUploaderEnhance
                        value={cancelVoucherS3Keys}
                        onChange={(keys) => { setCancelVoucherS3Keys(keys); setCancelVoucherError(false); }}
                        maxCount={6}
                        tag="pickup-appointment-cancel-voucher"
                    />
                    {cancelVoucherError && (
                        <div className="text-red-500 text-sm mt-1">{t('pickup.appointment.cancelVoucherRequired')}</div>
                    )}
                </div>
            </Modal>
        </>
    );


    // 如果启用拖拽，用 DndProvider 包裹；否则直接返回内容
    return enableDragSort ? (
        <DndProvider backend={HTML5Backend}>
            {tableContent}
        </DndProvider>
    ) : tableContent;
};

export default PickupAppointmentColumns;
